import { httpsCallable } from 'firebase/functions'
import type { FirebaseRepository } from '../../models/FirebaseRepository'

export interface TalkTrackAudioStatus {
  questionId: string
  slideId: string | null
  /** true when audio is present in storage */
  hasAudio: boolean
  /** speech-to-text score of the stored take, absent on pre-validation audio */
  similarity?: number
  /** what the stored take actually said, absent on pre-validation audio */
  transcript?: string
  /** set when the stored take could not be transcribed for verification */
  validationError?: string
  /** true when the stored audio scored below threshold or went unverified */
  needsReview: boolean
  /** uid of a reviewer who listened and attested this take, absent otherwise */
  validatedBy?: string
  /** ISO timestamp of when the reviewer attested, absent if not yet validated */
  validatedAt?: string
}

export interface CheckTalkTrackAudioResult {
  /** false when talk tracks are turned off, in which case audio is not required */
  talkTrackEnabled: boolean
  statuses: TalkTrackAudioStatus[]
}

/**
 * Asks the server which of a deck's narrated questions have validated audio in
 * storage. Used by publish-time validation.
 *
 * This is a function call rather than a local check because the audio lives at
 * a path keyed on md5 of the talk track text, and the browser has no md5. It
 * sits in its own module so tests can stub it without pulling in the rest of
 * the SlideQuestion firestore operations.
 */
export const checkTalkTrackAudio = async (
  repository: FirebaseRepository,
  { slideDeckId }: { slideDeckId: string }
): Promise<CheckTalkTrackAudioResult> => {
  const callable = httpsCallable<
    { slideDeckId: string; databaseId?: string },
    CheckTalkTrackAudioResult
  >(repository.functions, 'checkTalkTrackAudio')

  const { data } = await callable({
    slideDeckId,
    ...(repository.databaseId && { databaseId: repository.databaseId }),
  })

  return data
}

/**
 * Records that the current user listened to a talk track's stored audio and
 * attests it reads correctly. The server writes the attestation to the audio
 * object's metadata, so it is pinned to the current text: editing the script
 * clears it. After this resolves, re-running publish validation no longer
 * blocks on this track's low score.
 */
export const markTalkTrackAudioValidated = async (
  repository: FirebaseRepository,
  { slideDeckId, questionId }: { slideDeckId: string; questionId: string }
): Promise<{ validatedBy: string }> => {
  const callable = httpsCallable<
    { slideDeckId: string; questionId: string; databaseId?: string },
    { validatedBy: string }
  >(repository.functions, 'markTalkTrackAudioValidated')

  const { data } = await callable({
    slideDeckId,
    questionId,
    ...(repository.databaseId && { databaseId: repository.databaseId }),
  })

  return data
}
